import React from "react";
import { RouteComponentProps } from "react-router";
import styled from "styled-components";
import { Formik } from "formik";

import * as HTTP_CODE from "@config/httpStatuses";
import { URSSAF_FORM } from "@config/testIds/urssaf";

import { Territory, FrenchCommune, FrenchPostalCode } from "@typings/globals";

import { Button, Container, OptionPanel, TestWrapper } from "@ui/Atoms";
import { Row, Column } from "@ui/Atoms/Grid";
import { FormikForm } from "@ui/Molecules";

import { getQueryValue } from "@services/QueryParamsParser";

import { ScrollToTop } from "@containers/ScrollToTop";
import { showToast } from "@containers/Toastify";

import { useTranslate } from "@hooks/useTranslate";

import {
  initialValues,
  getValidationSchema,
  handleSubmit,
  myCleaningsRoute,
  fetchCountries,
  fetchCommunes,
  fetchPostalCodes,
} from "./UrssafRegistrationForm.helpers";
import { PersonalInformation, Address, BankAccount } from "./sections";

function UrssafRegistrationForm(
  props: RouteComponentProps,
): React.ReactElement {
  const translate = useTranslate("batmaid_pwa_urssaf");
  const translateGeneric = useTranslate("batmaid_pwa_generic");

  const [countries, setCountries] = React.useState<Territory[]>([]);
  const [communes, setCommunes] = React.useState<FrenchCommune[]>([]);
  const [postalCodes, setPostalCodes] = React.useState<FrenchPostalCode[]>([]);

  const hasCompletedBooking = Boolean(
    getQueryValue(props.location.search, "bookingCompleted"),
  );

  const handleFetchCountries = async () => {
    const { onError, onSuccess } = await fetchCountries();

    onSuccess(({ data }) => {
      setCountries(data);
    });

    onError(
      () => {
        showToast(translate("form_loading_fail"), {
          theme: "dark",
          toastId: "urssaf-form-loading-fail",
        });
      },
      { matchCode: HTTP_CODE.INTERNAL_SERVER_ERROR },
    );
  };

  const handleFetchCommunes = async (input: string) => {
    if (!input) return;

    const { onError, onSuccess } = await fetchCommunes(input);

    onSuccess(({ data }) => {
      setCommunes(data);
    });

    onError(
      () => {
        showToast(translate("form_loading_fail"), {
          theme: "dark",
          toastId: "urssaf-form-loading-fail",
        });
      },
      { matchCode: HTTP_CODE.INTERNAL_SERVER_ERROR },
    );
  };

  const handleFetchPostalCodes = async (input: string) => {
    if (input.length < 3) return;

    const { onError, onSuccess } = await fetchPostalCodes(input);

    onSuccess(({ data }) => {
      setPostalCodes(data);
    });

    onError(
      () => {
        showToast(translate("form_loading_fail"), {
          theme: "dark",
          toastId: "urssaf-form-loading-fail",
        });
      },
      { matchCode: HTTP_CODE.INTERNAL_SERVER_ERROR },
    );
  };

  React.useEffect(() => {
    handleFetchCountries();
  }, []);

  return (
    <StyledContainer>
      <ScrollToTop />
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(translateGeneric)}
        onSubmit={(values, { setSubmitting, setErrors }) => {
          handleSubmit({
            history: props.history,
            values,
            communes,
            postalCodes,
            setSubmitting,
            setErrors,
          });
        }}
      >
        {({
          values,
          isSubmitting,
          handleChange,
          setFieldValue,
          setFieldTouched,
          submitForm,
        }) => {
          return (
            <FormikForm>
              {hasCompletedBooking && (
                <OptionPanel
                  title={translate("registration_form_thank_you_title")}
                  subtitle={translate("registration_form_subtitle")}
                  textAlignLg={"center"}
                />
              )}
              <PersonalInformation
                values={values}
                countries={countries}
                communes={communes}
                hasCompletedBooking={hasCompletedBooking}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleFetchCommunes={handleFetchCommunes}
              />
              <Address
                values={values}
                postalCodes={postalCodes}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                handleFetchPostalCodes={handleFetchPostalCodes}
              />
              <BankAccount
                values={values}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
              {/** Form buttons */}
              <OptionPanel>
                <Row>
                  <Column lg={10} centered>
                    <ButtonsWrapper>
                      <TestWrapper testId={URSSAF_FORM.CANCEL_BUTTON}>
                        <Button
                          type="button"
                          variant={"textBlue"}
                          onClick={() => props.history.push(myCleaningsRoute)}
                        >
                          {translateGeneric("button_cancel")}
                        </Button>
                      </TestWrapper>
                      <TestWrapper testId={URSSAF_FORM.SUBMIT_BUTTON}>
                        <Button
                          type="button"
                          variant="green"
                          isLoading={isSubmitting}
                          onClick={submitForm}
                        >
                          {translateGeneric("submit")}
                        </Button>
                      </TestWrapper>
                    </ButtonsWrapper>
                  </Column>
                </Row>
              </OptionPanel>
            </FormikForm>
          );
        }}
      </Formik>
    </StyledContainer>
  );
}

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;

  ${props => props.theme.breakpoints.desktop} {
    padding-top: ${props => props.theme.margins.base_x4};
    padding-bottom: ${props => props.theme.margins.base_x4};
    display: block;
  }
`;

export { UrssafRegistrationForm, StyledContainer };
